import { z } from 'zod';
import {
  baseDocumentFields,
  emailLower,
  objectIdString,
  phoneE164,
  sha256Hex,
} from './common.js';
import {
  closedLostReason,
  funnelStage,
  qualificationStatus,
} from './enums.js';

// Campos de origem truncam em vez de rejeitar. O Gerenciador da Meta preenche
// esses valores por macro ({{campaign.name}}, {{ad.name}}), e nome de campanha
// não tem limite de tamanho lá · com `.max()` puro, uma campanha de nome longo
// derrubaria o submit inteiro no 400 e o lead se perderia. Perder o final de um
// utm_content é aceitável; perder o lead não é.
const boundedText = (limit: number) =>
  z
    .string()
    .transform((v) => v.trim().slice(0, limit))
    .optional();

export const leadSource = z
  .object({
    utmSource: boundedText(120),
    utmMedium: boundedText(120),
    utmCampaign: boundedText(120),
    utmTerm: boundedText(120),
    utmContent: boundedText(120),
    fbclid: boundedText(200),
    referrer: boundedText(500),
    /** URL do primeiro pageview da sessão (onde os parâmetros do anúncio ainda
     *  existiam). Preenchida a partir de sessionStorage · ver web/lib/source-store.ts. */
    entryUrl: boundedText(500),
    /** URL da página do formulário. Usada como `eventSourceUrl` no Meta CAPI. */
    landingUrl: boundedText(500),
  })
  .strict();

export type LeadSource = z.infer<typeof leadSource>;

// Client-side context captured at submit. Used to enrich Meta CAPI events
// (client_ip_address, client_user_agent, fbp, fbc).
export const leadClient = z
  .object({
    ip: z.string().max(45).optional(),
    userAgent: z.string().max(500).optional(),
    fbp: z.string().max(200).optional(),
    fbc: z.string().max(200).optional(),
  })
  .strict();

export type LeadClient = z.infer<typeof leadClient>;

// Manual override created when a broker promotes a partially_qualified lead
// to fully_qualified, with a written reason. The override is persisted (not
// re-evaluated) so the audit trail survives schema changes to the qualifier.
export const leadManualQualificationOverride = z
  .object({
    reason: z.string().trim().min(5).max(500),
    overriddenByFirebaseUid: z.string().min(1),
    /** Whether the override came from the broker themselves or from an admin
     * operating cross-broker. The corretor's panel shows a different notice
     * when it was an admin so the corretor knows it wasn't their action. */
    overriddenByRole: z.enum(['broker', 'admin']).optional(),
    /** Display name of the admin/broker who promoted (for the broker panel
     * to show "Qualificado pelo admin João" instead of just the uid). */
    overriddenByName: z.string().max(160).optional(),
    overriddenAt: z.coerce.date(),
  })
  .strict();

export type LeadManualQualificationOverride = z.infer<
  typeof leadManualQualificationOverride
>;

export const lead = z
  .object({
    ...baseDocumentFields,
    brokerId: objectIdString,
    /**
     * Vertical key snapshotted at submit time (e.g. 'mcmv-imobiliario').
     * Denormalized from broker.verticalId so analytics and per-vertical
     * filters don't require a join. Optional during rollout: legacy leads
     * (pre-multi-vertical) have no value and are treated as MCMV.
     */
    verticalKey: z.string().min(2).max(40).optional(),
    /** Optional · leads from the broker's landing page may not pick a property. */
    propertyId: objectIdString.optional(),
    name: z.string().trim().min(2).max(160),
    email: emailLower,
    phone: phoneE164,
    /** SHA-256(lowercase, trimmed email) · for Meta CAPI dedup and matching. */
    emailHash: sha256Hex,
    /** SHA-256(digits-only E.164 without "+") · for Meta CAPI dedup and matching. */
    phoneHash: sha256Hex,
    qualificationStatus: qualificationStatus,
    /** Present when broker manually promoted a partially_qualified lead. */
    manualQualificationOverride: leadManualQualificationOverride.optional(),
    funnelStage: funnelStage,
    closedLostReason: closedLostReason.optional(),
    source: leadSource,
    client: leadClient,
    lastContactedAt: z.coerce.date().optional(),
    /**
     * Primeiro toque do lead no botão de WhatsApp da tela de resultado.
     * `null` = rastreado e ainda não abriu · ausente = lead anterior ao
     * rastreio (não dá pra afirmar nada).
     */
    whatsappClickedAt: z.coerce.date().nullable().optional(),
    notes: z.string().max(4000).optional(),
  })
  .strict();

export type Lead = z.infer<typeof lead>;
